import { useCallback, useEffect, useRef, useState } from 'react';
import { TimerMode, useAppDispatch, useAppSelector, useTimer } from '../../hooks';
import { openRequestReviewModal } from '../../redux/reducers';
import { ComponentSize, MeetingState, Permissions, PracticeProspect, SemanticColor, TextColor } from '../../types';
import { formatSecondsToDuration, isProspectLocked } from '../../utils';
import { ButtonColor, ButtonVariant, Icon, Icons, TextButton, Typography, TypographyType } from '../shared';

const UPDATE_TIME_INTERVAL = 1000; // 1 second

interface DialerStatusProps {
  callSid: string;
  currProspect: PracticeProspect;
  isDaily: boolean;
  meetingState: MeetingState;
  handleRedial: () => void;
  isRedialing?: boolean;
}

const DialerStatus = ({
  callSid,
  currProspect,
  meetingState,
  isDaily,
  handleRedial,
  isRedialing,
}: DialerStatusProps) => {
  // Current time of the call in seconds.
  const [currentTime, setCurrentTime] = useState(0);

  const dispatch = useAppDispatch();
  const permissions = useAppSelector((state) => state.auth.user?.permissions);

  // Tracks the start time of the call, persisting across state resets.
  const startTimeRef = useRef(0);

  // The user can flag the call if they have sufficient permissions and the call sid exists,
  // indicating that the call has connected without any errors.
  const canFlag = permissions?.includes(Permissions.FLAG_CALL) && !!callSid;

  const updateTime = useCallback(() => {
    const currTime = new Date().getTime();
    const elapsedTime = (currTime - startTimeRef.current) / UPDATE_TIME_INTERVAL;
    setCurrentTime(elapsedTime);
  }, []);

  // Manages the interval that updates the elapsed time every second.
  const { startTimer: startInterval, stopTimer: stopInterval } = useTimer({
    callback: updateTime,
    delay: UPDATE_TIME_INTERVAL,
    mode: TimerMode.INTERVAL,
  });

  // Update the refs and interval when the call state changes.
  useEffect(() => {
    if (
      (isDaily && meetingState === MeetingState.JOINING_MEETING) ||
      (!isDaily && meetingState === MeetingState.CONNECTING_TO_MIC)
    ) {
      // Reset the current time when the call starts.
      setCurrentTime(0);
    } else if (meetingState === MeetingState.JOINED_MEETING || meetingState === MeetingState.ERROR) {
      // Set the start time and start the interval when the call is connected.
      startTimeRef.current = new Date().getTime();
      startInterval();
    } else if (meetingState === MeetingState.HANGING_UP) {
      // Stop the interval when the call is disconnected.
      stopInterval();
    }
  }, [isDaily, meetingState, startInterval, stopInterval]);

  const renderContent = () => {
    if (
      meetingState === MeetingState.JOINED_MEETING ||
      meetingState === MeetingState.ERROR ||
      meetingState === MeetingState.HANGING_UP
    ) {
      return (
        <>
          <Typography type={TypographyType.BASE}>{formatSecondsToDuration(currentTime)}</Typography>
          <div className="flex w-full items-center gap-2">
            <Icons size={ComponentSize.MEDIUM} icon={Icon.PHONE_OUTGOING} color={SemanticColor.PRIMARY} />
            <Typography type={TypographyType.BASE} color="text-primary">
              Connected
            </Typography>
          </div>
        </>
      );
    }

    const showRedialing = meetingState !== MeetingState.JOINING_MEETING && isRedialing;
    if (meetingState === MeetingState.IDLE || showRedialing) {
      return (
        <>
          <Typography type={TypographyType.BASE}>{formatSecondsToDuration(currentTime)}</Typography>
          <div className="flex w-full flex-col gap-2">
            {canFlag && (
              <TextButton
                fullWidth
                size={ComponentSize.X_SMALL}
                startIcon={<Icons fillColor="fill-orange-500" icon={Icon.FLAG} />}
                text="Request review"
                variant={ButtonVariant.SOFT}
                color={ButtonColor.NEUTRAL}
                onClick={() => {
                  dispatch(openRequestReviewModal({ callSid }));
                }}
              />
            )}
            <TextButton
              disabled={isProspectLocked(currProspect)}
              fullWidth
              loading={showRedialing}
              tooltip={showRedialing ? 'Connecting to mic...' : undefined}
              size={ComponentSize.X_SMALL}
              startIcon={Icon.PHONE}
              text="Redial"
              variant={ButtonVariant.SOFT}
              onClick={handleRedial}
            />
          </div>
        </>
      );
    }

    return (
      <div className="flex w-full items-center gap-2">
        <Icons size={ComponentSize.MEDIUM} color={TextColor.SECONDARY} icon={Icon.PHONE_OUTGOING} />
        <Typography type={TypographyType.BASE}>Ringing...</Typography>
      </div>
    );
  };

  return (
    <div className="flex w-full items-center gap-4">
      {renderContent()}
      <Icons
        size={ComponentSize.MEDIUM}
        icon={Icon.INFO}
        tooltip="We recommend calling from a quiet place or using headphones"
        color={TextColor.SECONDARY}
      />
    </div>
  );
};

export default DialerStatus;
